import { cva, type VariantProps } from 'class-variance-authority';
import {
  Ban,
  CalendarX2,
  CircleAlert,
  CircleCheck,
  Info,
  Leaf,
  Percent,
  Tag,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium leading-5 [&_svg]:size-3.5 [&_svg]:shrink-0 comfortable:px-3 comfortable:py-1 comfortable:text-body comfortable:leading-6 comfortable:[&_svg]:size-5',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-surface-sunken text-fg',
        primary: 'border-transparent bg-primary-subtle text-primary-text',
        accent: 'border-transparent bg-accent-subtle text-accent-text',
        outline: 'border-border-strong bg-transparent text-fg',
        fresh: 'border-transparent bg-state-fresh-subtle text-state-fresh',
        markdown: 'border-transparent bg-state-markdown-subtle text-state-markdown',
        clearance: 'border-transparent bg-state-clearance-subtle text-state-clearance',
        expired: 'border-transparent bg-state-expired-subtle text-state-expired',
        blocked: 'border-transparent bg-state-blocked-subtle text-state-blocked',
        success: 'border-transparent bg-state-success-subtle text-state-success',
        warning: 'border-transparent bg-state-warning-subtle text-state-warning',
        danger: 'border-transparent bg-state-danger-subtle text-state-danger',
      },
      solid: { true: '', false: '' },
    },
    compoundVariants: [
      { tone: 'fresh', solid: true, className: 'bg-state-fresh text-state-fresh-foreground' },
      {
        tone: 'markdown',
        solid: true,
        className: 'bg-state-markdown text-state-markdown-foreground',
      },
      {
        tone: 'clearance',
        solid: true,
        className: 'bg-state-clearance text-state-clearance-foreground',
      },
      { tone: 'expired', solid: true, className: 'bg-state-expired text-state-expired-foreground' },
      { tone: 'blocked', solid: true, className: 'bg-state-blocked text-state-blocked-foreground' },
      { tone: 'success', solid: true, className: 'bg-state-success text-state-success-foreground' },
      { tone: 'warning', solid: true, className: 'bg-state-warning text-state-warning-foreground' },
      { tone: 'danger', solid: true, className: 'bg-state-danger text-state-danger-foreground' },
      { tone: 'primary', solid: true, className: 'bg-primary text-primary-foreground' },
    ],
    defaultVariants: { tone: 'neutral', solid: false },
  },
);

export type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants>;

export function Badge({ className, tone, solid, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone, solid }), className)} {...props} />;
}

// ---------------------------------------------------------------- StateBadge

/** Lot / stock states (plan: fresh → markdown → clearance → expired; blocked = held). */
export type StockState = 'fresh' | 'markdown' | 'clearance' | 'expired' | 'blocked';

export const STOCK_STATES: Record<StockState, { label: string; icon: LucideIcon }> = {
  fresh: { label: 'Fresh', icon: Leaf },
  markdown: { label: 'Markdown', icon: Tag },
  clearance: { label: 'Clearance', icon: Percent },
  expired: { label: 'Expired', icon: CalendarX2 },
  blocked: { label: 'Blocked', icon: Ban },
};

export interface StateBadgeProps extends Omit<ComponentProps<'span'>, 'children'> {
  state: StockState;
  /** Solid fill for emphasis (e.g. on a label preview); subtle tint by default. */
  solid?: boolean;
  /** Override the label (the icon stays, so meaning never relies on colour alone). */
  children?: ReactNode;
}

export function StateBadge({ state, solid = false, children, ...props }: StateBadgeProps) {
  const { label, icon: Icon } = STOCK_STATES[state];
  return (
    <Badge tone={state} solid={solid} data-state={state} {...props}>
      <Icon aria-hidden />
      {children ?? label}
    </Badge>
  );
}

// ---------------------------------------------------------------- StatusBadge

export type Status = 'success' | 'warning' | 'danger' | 'neutral';

const STATUS_ICONS: Record<Status, LucideIcon> = {
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
  neutral: Info,
};

export interface StatusBadgeProps extends ComponentProps<'span'> {
  status: Status;
  solid?: boolean;
}

/** Generic outcome badge ("Passed", "Needs review", "Failed"). Always icon + text. */
export function StatusBadge({ status, solid = false, children, ...props }: StatusBadgeProps) {
  const Icon = STATUS_ICONS[status];
  return (
    <Badge
      tone={status}
      solid={status === 'neutral' ? false : solid}
      data-status={status}
      {...props}
    >
      <Icon aria-hidden />
      {children}
    </Badge>
  );
}
